import React, { useState, useEffect } from 'react';
import ConfirmDialog from './ConfirmDialog';
import './RssManagement.css';
import { API_CONFIG } from '../../config/api';
import { getHeaders, getFormDataHeaders } from '../../utils/api';

interface RSSFeed {
  id: number;
  topicId: string;
  topicName: string;
  url: string;
  intervalMinutes: number;
  logoUrl: string;
}

interface EditFeedData {
  id: number;
  topic: string;
  topicId: string;
  url: string;
  intervalMinutes: number;
  logoUrl: string;
}

const RSSManagement = () => {
  const [feeds, setFeeds] = useState<RSSFeed[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFeed, setEditingFeed] = useState<RSSFeed | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; url: string } | null>(null);
  const [formData, setFormData] = useState<EditFeedData>({
    id: 0,
    topic: '',
    topicId: '',
    url: '',
    intervalMinutes: 30,
    logoUrl: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRSSFeeds();
    fetchTopics();
  }, []);

  const fetchTopics = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TOPICS}`, {
        headers: getHeaders()
      });
      if (!response.ok) {
        throw new Error('Failed to fetch topics');
      }
      const data = await response.json();
      setTopics(data.topics || []);
    } catch (err) {
      console.error('Error fetching topics:', err);
    }
  };

  const fetchRSSFeeds = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RSS.BASE}`, {
        headers: getHeaders()
      });
      if (!response.ok) {
        throw new Error('Failed to fetch RSS feeds');
      }
      const data = await response.json();
      setFeeds(data);
    } catch (err) {
      setError('Failed to load RSS feeds');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const feed = feeds.find(f => f.id === id);
    if (feed) {
      setConfirmDelete({ id, url: feed.url });
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RSS.DELETE}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ id: confirmDelete.id }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete RSS feed');
      }

      await fetchRSSFeeds();
      setConfirmDelete(null);
    } catch (err) {
      setError('Failed to delete RSS feed');
      console.error('Error:', err);
    }
  };

  const handleEdit = (feed: RSSFeed) => {
    setEditingFeed(feed);
    setFormData({
      id: feed.id,
      topic: feed.topicName,
      topicId: feed.topicId,
      url: feed.url,
      intervalMinutes: feed.intervalMinutes,
      logoUrl: feed.logoUrl
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingFeed) return;

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RSS.EDIT}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          id: formData.id,
          topicId: formData.topicId,
          topic: formData.topic,
          url: formData.url,
          intervalMinutes: formData.intervalMinutes,
          logoUrl: formData.logoUrl
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update RSS feed');
      }

      await fetchRSSFeeds();
      setShowEditModal(false);
      setEditingFeed(null);
    } catch (err) {
      setError('Failed to update RSS feed');
      console.error('Error:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Create FormData object
      const formDataObj = new FormData();
      formDataObj.append('topic', formData.topicId);
      formDataObj.append('url', formData.url);
      formDataObj.append('intervalMinutes', formData.intervalMinutes.toString());
      formDataObj.append('logoUrl', formData.logoUrl);

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RSS.BASE}`, {
        method: 'POST',
        headers: getFormDataHeaders(),
        body: formDataObj,
      });

      if (!response.ok) {
        throw new Error(` ${response.statusText}`);
      }

      // Refresh the feeds list
      await fetchRSSFeeds();
      
      // Reset form and close modal
      setFormData({ id: 0, topic: '', topicId: '', url: '', intervalMinutes: 30, logoUrl: '' });
      setShowAddModal(false);
    } catch (err) {
      setError(`Failed to add RSS feed ${err}`);
      console.error('Error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return <div className="loading">Loading RSS feeds...</div>;
  }

  return (
    <div className="content-section">
      <div className="content-header">
        <h2>RSS Feeds</h2>
        <p>Manage RSS feed sources and update intervals</p>
        <button className="add-button" onClick={() => setShowAddModal(true)}>Add RSS Feed</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="rss-table-container">
        <table className="rss-table">
          <thead>
            <tr>
              <th>Topic ID</th>
              <th>Topic Name</th>
              <th>URL</th>
              <th>Logo</th>
              <th>Interval (minutes)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {feeds.map(feed => (
              <tr key={feed.id}>
                <td>{feed.topicId}</td>
                <td>{feed.topicName}</td>
                <td>
                  <a href={feed.url} target="_blank" rel="noopener noreferrer">
                    {feed.url}
                  </a>
                </td>
                <td>
                  {feed.logoUrl && (
                    <div className="logo-preview">
                      <img 
                        src={feed.logoUrl} 
                        alt={`${feed.topicName} logo`} 
                        className="feed-logo"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                      <a href={feed.logoUrl} target="_blank" rel="noopener noreferrer" className="logo-link">
                        View Logo
                      </a>
                    </div>
                  )}
                </td>
                <td>{feed.intervalMinutes}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="action-btn edit"
                      onClick={() => handleEdit(feed)}
                    >
                      Edit
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDelete(feed.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add RSS Feed</h3>
              <button 
                className="close-button" 
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="topic">Topic:</label>
                <input
                  type="text"
                  id="topic"
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  list="topics-list"
                  required
                />
                <datalist id="topics-list">
                  {topics.map((topic, index) => (
                    <option key={index} value={topic} />
                  ))}
                </datalist>
              </div>
              <div className="form-group">
                <label htmlFor="url">URL:</label>
                <input
                  type="url"
                  id="url"
                  name="url"
                  value={formData.url}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="intervalMinutes">Update Interval (minutes):</label>
                <input
                  type="number"
                  id="intervalMinutes"
                  name="intervalMinutes"
                  value={formData.intervalMinutes}
                  onChange={handleInputChange}
                  min="0"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="logoUrl">Logo URL:</label>
                <input
                  type="url"
                  id="logoUrl"
                  name="logoUrl"
                  value={formData.logoUrl}
                  onChange={handleInputChange}
                />
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={submitting}
                >
                  {submitting ? 'Adding...' : 'Add Feed'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit RSS Feed</h3>
              <button 
                className="close-button" 
                onClick={() => {
                  setShowEditModal(false);
                  setEditingFeed(null);
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label htmlFor="edit-topic">Topic:</label>
                <input
                  type="text"
                  id="edit-topic"
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  list="topics-list"
                  required
                />
                <datalist id="topics-list">
                  {topics.map((topic, index) => (
                    <option key={index} value={topic} />
                  ))}
                </datalist>
              </div>
              <div className="form-group">
                <label htmlFor="edit-url">URL:</label>
                <input
                  type="url"
                  id="edit-url"
                  name="url"
                  value={formData.url}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-intervalMinutes">Update Interval (minutes):</label>
                <input
                  type="number"
                  id="edit-intervalMinutes"
                  name="intervalMinutes"
                  value={formData.intervalMinutes}
                  onChange={handleInputChange}
                  min="0"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-logoUrl">Logo URL:</label>
                <input
                  type="url"
                  id="edit-logoUrl"
                  name="logoUrl"
                  value={formData.logoUrl}
                  onChange={handleInputChange}
                />
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingFeed(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                >
                  Update Feed
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete the RSS feed "${confirmDelete?.url}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
        confirmText="Delete"
        type="delete"
      />
    </div>
  );
};

export default RSSManagement; 